import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Loader2, Plus, Sparkles, Trash2 } from 'lucide-react'
import { useAdminCarBrands, useAdminCarModels, useAdminCarGenerations, useCreateGeneration, useDeleteGeneration } from '../../../hooks/use-admin-cars'
import { DeleteConfirmDialog } from '../components/delete-confirm-dialog'

export function GenerationsPage() {
  const { data: brands } = useAdminCarBrands()
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>(undefined)
  const activeBrandId = selectedBrandId ?? (brands?.[0]?.id || 0)

  const { data: models } = useAdminCarModels(activeBrandId)
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>(undefined)
  const activeModelId = selectedModelId ?? (models?.[0]?.id || 0)

  const { data: generations, isLoading } = useAdminCarGenerations(activeModelId)
  const createGeneration = useCreateGeneration()
  const deleteGeneration = useDeleteGeneration()

  const [open, setOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any>(null)
  const [name, setName] = useState('')
  const [startYear, setStartYear] = useState('2021')
  const [endYear, setEndYear] = useState('')

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !activeModelId) return
    await createGeneration.mutateAsync({
      modelId: activeModelId,
      name,
      startYear: Number(startYear),
      endYear: endYear ? Number(endYear) : undefined,
    })
    setName('')
    setOpen(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Car Generations Catalog</h1>
          <p className="text-sm text-muted-foreground">Cascading Brand → Model → Generation structure for vehicle year cycles.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button disabled={!activeModelId} className="gap-2">
              <Plus className="size-4" /> Add Generation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Generation under Model</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Generation Name *</label>
                <Input placeholder="e.g. 2nd Gen (AN160)" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Start Year *</label>
                  <Input type="number" value={startYear} onChange={(e) => setStartYear(e.target.value)} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">End Year (Leave empty if ongoing)</label>
                  <Input type="number" value={endYear} onChange={(e) => setEndYear(e.target.value)} />
                </div>
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createGeneration.isPending}>
                  {createGeneration.isPending ? <Loader2 className="size-4 animate-spin" /> : 'Save Generation'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Cascading Selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">1. Select Brand</label>
          <select value={String(activeBrandId)} onChange={(e) => { setSelectedBrandId(Number(e.target.value)); setSelectedModelId(undefined); }} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            {(brands || []).map((b: any) => (
              <option key={b.id} value={String(b.id)}>{b.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">2. Select Model</label>
          <select value={String(activeModelId)} onChange={(e) => setSelectedModelId(Number(e.target.value))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            {(models || []).map((m: any) => (
              <option key={m.id} value={String(m.id)}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Generations Table */}
      {isLoading ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading vehicle generations...</p>
        </div>
      ) : (
        <Card className="overflow-hidden border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-4">Generation Name</th>
                <th className="p-4">Year Range</th>
                <th className="p-4">Variants</th>
                <th className="p-4">Listings</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(generations || []).map((g: any) => {
                const isIndependent = (g._count?.variants ?? 0) === 0 && (g._count?.listings ?? 0) === 0
                return (
                  <tr key={g.id} className="hover:bg-accent/20 transition-colors">
                    <td className="p-4 font-bold flex items-center gap-2">
                      <Sparkles className="size-4 text-primary" />
                      <span>{g.name}</span>
                    </td>
                    <td className="p-4 font-semibold">{g.startYear} - {g.endYear || 'Present'}</td>
                    <td className="p-4 font-semibold">{g._count?.variants ?? 0}</td>
                    <td className="p-4 font-semibold">{g._count?.listings ?? 0}</td>
                    <td className="p-4">
                      <Badge className={g.isActive ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}>
                        {g.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(g)}
                        className={`size-8 p-0 ${
                          isIndependent
                            ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-500/10'
                            : 'text-muted-foreground/50 hover:text-amber-600 hover:bg-amber-500/10'
                        }`}
                        title={
                          isIndependent
                            ? `Delete independent generation "${g.name}"`
                            : `Cannot delete: Has ${g._count?.variants || 0} variants and ${g._count?.listings || 0} listings`
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteGeneration.mutateAsync({ id: deleteTarget.id, modelId: activeModelId })
          }}
          title={`Delete Generation: ${deleteTarget.name}`}
          entityName={deleteTarget.name}
          entityType="Generation"
          isIndependent={(deleteTarget._count?.variants ?? 0) === 0 && (deleteTarget._count?.listings ?? 0) === 0}
          blockingReason={`Cannot delete generation "${deleteTarget.name}" because it has ${deleteTarget._count?.variants || 0} variant(s) and ${deleteTarget._count?.listings || 0} listing(s) attached. Only independent generations can be deleted.`}
          dependencyDetails={[
            { label: 'Variants', count: deleteTarget._count?.variants ?? 0 },
            { label: 'Listings', count: deleteTarget._count?.listings ?? 0 },
          ]}
        />
      )}
    </div>
  )
}
